import React, { useEffect, useState } from "react";

/**
 * FontLoader component ensures fonts are properly loaded post-authentication
 * This helps solve the issue of fallback fonts being displayed after authentication
 */
const FontLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    // Create a function to check if Roboto font is loaded
    const checkFontLoaded = () => {
      // This timeout ensures we give the browser some time to load fonts
      setTimeout(() => {
        // Force font reflow
        document.body.style.fontFamily =
          "Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif";

        // Apply and remove a class to trigger font reflow
        document.body.classList.add("fonts-loaded");
        setFontsLoaded(true);
      }, 100);
    };

    // Use Font Loading API if supported, otherwise use timeout
    if ("fonts" in document) {
      // Wait for fonts to be loaded
      document.fonts.ready.then(() => {
        checkFontLoaded();
      });
    } else {
      checkFontLoaded();
    }

    // Clean up
    return () => {
      document.body.classList.remove("fonts-loaded");
    };
  }, []);

  // Add a style element to ensure font is properly displayed
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .fonts-loaded body {
        font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return <>{children}</>;
};

export default FontLoader;
